import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormType, formSchema } from '../../formSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem } from 'components/ui/form';
import { InputGs, SelectGs } from 'components/ui/Forms';
import { useMutation, useQuery } from 'react-query';
import { useErrorHandler } from 'Exceptions/ErrorHandler';
import { useNavigate } from 'react-router-dom';
import { Carrier } from 'contracts/Carriers';
import { Company } from 'contracts/Companies';
import { Button } from 'components/ui/ButtonGs';
import { createUser } from 'hooks/api/Users/createUser';
import { notify } from 'services';
import { ActivityIndicator } from 'styles/components';
import MultipleSelector from 'components/ui/multi-select';
import { Client } from 'contracts/Client';
import { getClients, getCarriers, getCompanies } from 'hooks/api/Users/Options';
import { CARRIER_ACCESS_ROLE_OPTIONS } from 'lib/viagens-controle-permissions';

interface SelectOption {
  name: string;
  value: string;
  label: string;
}
interface OptionsType {
  [key: string]: SelectOption[];
}
type UserTypes =
  | 'carrierMember'
  | 'warehouseMember'
  | 'admin'
  | 'clientMember'
  | 'planner'
  | 'consultMember';

type UserTypesWithAssociation =
  | 'carrierMember'
  | 'warehouseMember'
  | 'clientMember';

const userOptions = [
  { name: 'Usuário de transportadora', value: 'carrierMember' },
  { name: 'Usuário de armazém', value: 'warehouseMember' },
  { name: 'Usuário de cliente', value: 'clientMember' },
  { name: 'Planejador', value: 'planner' },
  { name: 'Usuário de consulta', value: 'consultMember' },
  { name: 'Administrador', value: 'admin' },
];

const fieldNameDict: Record<UserTypesWithAssociation, string> = {
  carrierMember: 'carrierId',
  warehouseMember: 'companyId',
  clientMember: 'clientId',
};

// Helper function para verificar se o userType precisa de associação
const requiresAssociation = (
  userType: UserTypes
): userType is UserTypesWithAssociation => {
  return userType in fieldNameDict;
};

const routeDictionary = {
  carrierMember: 'transportadora',
  warehouseMember: 'armazem',
  clientMember: 'clientes',
  admin: '',
  planner: '',
  consultMember: '',
};

const CreateUsuarioForm = () => {
  const { errorHandler } = useErrorHandler();
  const navigate = useNavigate();
  const [userType, setUserType] = useState<UserTypes | null>(null);

  const { data: carriers } = useQuery<Carrier[]>({
    queryKey: 'carriers',
    queryFn: getCarriers,
    onError: errorHandler,
  });
  const { data: companies } = useQuery<Company[]>({
    queryKey: 'companies',
    queryFn: getCompanies,
    onError: errorHandler,
  });
  const { data: clients } = useQuery<Client[]>({
    queryKey: 'clients',
    queryFn: getClients,
    onError: errorHandler,
  });

  const form = useForm<FormType>({ resolver: zodResolver(formSchema) });

  const options: OptionsType = useMemo(() => {
    if (!carriers || !companies || !clients)
      return { carrierMember: [], warehouseMember: [], clientMember: [] };
    return {
      carrierMember: carriers.map((item) => ({
        name: item.tradeName,
        label: item.tradeName,

        value: `${item.id}`,
      })),
      warehouseMember: companies.map((item) => ({
        name: item.tradeName,
        label: item.tradeName,
        value: `${item.id}`,
      })),
      clientMember: clients.map((item) => ({
        name: item.tradeName,
        label: item.tradeName,
        value: `${item.id}`,
      })),
    };
  }, [companies, carriers, clients]);

  const mutation = useMutation(createUser, {
    onSuccess: () => {
      notify('success', 'Usuario cadastrado com sucesso');
      navigate(
        `/configuracoes/usuarios/${
          routeDictionary[userType as keyof typeof routeDictionary]
        }`
      );
    },
    onError: (error) => {
      errorHandler(error);
    },
  });

  function handleSubmit(data: FormType) {
    // Apenas processa campos de associação para tipos que precisam
    if (userType && requiresAssociation(userType)) {
      const field = fieldNameDict[userType] as
        | 'carrierId'
        | 'companyId'
        | 'clientId';
      const formatData = {
        ...data,
        [field]: data[field]?.map((item) => item.value),
      };
      mutation.mutate(formatData);
    } else if (userType === 'planner') {
      const payload = {
        user: {
          name: data.user.name,
          email: data.user.email,
          password: data.user.password,
        },
        material_id: data.material_id,
        type: data.type,
      };
      mutation.mutate(payload);

    } else if (userType === 'admin' || userType === 'consultMember') {
      const payload = {
        user: {
          name: data.user.name,
          email: data.user.email,
          password: data.user.password,
        },
        type: data.type,
      };
      mutation.mutate(payload);
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="grid grid-cols-4 gap-4"
      >
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <SelectGs
              {...field}
              onValueChange={(e: UserTypes) => {
                setUserType(e);
                form.setValue('type', e);
                if (e === 'carrierMember') {
                  form.setValue('accessRoleName', 'carrierMember');
                } else {
                  form.setValue('accessRoleName', undefined);
                }
              }}
              label="Tipo de usuário"
              content={userOptions}
            />
          )}
        />

        <FormField
          control={form.control}
          name="user.name"
          render={({ field }) => <InputGs {...field} label="Nome do usuário" />}
        />
        <FormField
          control={form.control}
          name="user.email"
          render={({ field }) => <InputGs {...field} label="E-mail" />}
        />
        <FormField
          control={form.control}
          name="user.password"
          render={({ field }) => (
            <InputGs {...field} type="password" label="Senha" />
          )}
        />

        {userType === 'carrierMember' && (
          <FormField
            control={form.control}
            name="accessRoleName"
            render={({ field }) => (
              <SelectGs
                {...field}
                value={field.value ?? 'carrierMember'}
                onValueChange={(value: string) => {
                  form.setValue('accessRoleName', value as FormType['accessRoleName']);
                }}
                label="Perfil de acesso"
                content={CARRIER_ACCESS_ROLE_OPTIONS.map((option) => ({
                  name: option.label,
                  value: option.value,
                }))}
              />
            )}
          />
        )}

        <div className="col-span-full">
          {userType && requiresAssociation(userType) && (
            <FormField
              control={form.control}
              name={fieldNameDict[userType] as any}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <div className="flex flex-col gap-2">
                        <label className="font-GilroySemibold text-sm text-slate-800">
                          Selecione...
                        </label>
                        <MultipleSelector
                          onChange={(e) => {
                            if (userType && requiresAssociation(userType)) {
                              form.setValue(fieldNameDict[userType] as any, e);
                            }
                          }}
                          options={
                            options[userType] as unknown as {
                              label: string;
                              value: string;
                            }[]
                          }
                          inputProps={{
                            autoFocus: false,
                            ref: field.ref,
                          }}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                );
              }}
            />
          )}
        </div>
        <div className="col-span-4">
          <div className="flex gap-4 justify-end">
            <Button.Root
              className="bg-orange-100 text-orange-600"
              size="sm"
              type="button"
              onClick={() => navigate(-1)}
            >
              Cancelar
            </Button.Root>
            <Button.Root className="bg-orange-500" size="sm" type="submit">
              {mutation.isLoading ? <ActivityIndicator /> : 'Salvar'}
            </Button.Root>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default CreateUsuarioForm;
